import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Settings, Save, Flame, AlertTriangle, Calendar, Percent, Wallet } from "lucide-react";

const BASE_OPTIONS = [
  {
    value: "invoice_amount",
    label: "Per invoice",
    desc: "Penalty accrues on the overdue invoice's amount only. Standard for most B2B contracts.",
  },
  {
    value: "project_total",
    label: "Per contract",
    desc: "Penalty accrues on the full project / contract value, regardless of which line is late. Used when the contract explicitly says so.",
  },
];

const PaymentPenaltySettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    default_rate_per_day: 0.1,
    default_grace_period_days: 5,
    default_base: "invoice_amount",
    note: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/payment-penalty");
        setSettings({
          enabled: !!res.data.enabled,
          default_rate_per_day: Number(res.data.default_rate_per_day ?? 0.1),
          default_grace_period_days: Number(res.data.default_grace_period_days ?? 5),
          default_base: res.data.default_base || "invoice_amount",
          note: res.data.note || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payment-penalty", settings);
      toast.success("Penalty settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Simple worked example so managers see the math at a glance.
  const exampleAmount = 1000;
  const exampleDaysLate = 15;
  const daysCharged = Math.max(0, exampleDaysLate - settings.default_grace_period_days);
  const examplePenalty = (exampleAmount * daysCharged * settings.default_rate_per_day) / 100;
  const annualEquivalent = settings.default_rate_per_day * 365;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="payment-penalty-settings-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
            <Flame className="h-7 w-7 text-red-500" />
            Late-payment penalty
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Portal-wide defaults. Each project can override <span className="font-mono text-zinc-300">penalty_rate_per_day</span> and{" "}
            <span className="font-mono text-zinc-300">grace_period_days</span> on its own settings.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      {/* Master switch */}
      <Card className={settings.enabled ? "bg-red-950/20 border-red-900/40" : "bg-zinc-900/50 border-zinc-800"}>
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`h-5 w-5 ${settings.enabled ? "text-red-500" : "text-zinc-600"}`} />
            <div>
              <p className="text-sm font-semibold text-zinc-50">Penalty calculation</p>
              <p className="text-xs text-zinc-500">
                {settings.enabled
                  ? "On — overdue invoices accrue penalty according to the rules below."
                  : "Off — overdue invoices show only days late, no $-penalty is computed."}
              </p>
            </div>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
          />
        </CardContent>
      </Card>

      {/* Settings matrix */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
            <Settings className="h-4 w-4 text-orange-500" /> Defaults
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Rate */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div className="md:col-span-1">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Percent className="h-3.5 w-3.5 text-amber-400" /> Rate per day
              </Label>
              <p className="text-xs text-zinc-500 mt-1">% per day of overdue, applied to the base.</p>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.default_rate_per_day}
                onChange={(e) => setSettings({ ...settings, default_rate_per_day: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-[200px]"
              />
              <p className="text-xs text-zinc-500">
                ≈ <span className="font-mono text-zinc-400">{annualEquivalent.toFixed(1)}%</span> annual.{" "}
                Reference points:{" "}
                <span className="text-zinc-400">0.1 = UA B2B standard (~36% annual)</span>,{" "}
                <span className="text-zinc-400">0.5 = aggressive (~180% annual)</span>,{" "}
                <span className="text-zinc-400">1.0 = max deterrent (~365% annual)</span>.
              </p>
            </div>
          </div>

          <div className="border-t border-zinc-800" />

          {/* Grace */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div className="md:col-span-1">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-sky-400" /> Grace period
              </Label>
              <p className="text-xs text-zinc-500 mt-1">Days after the due date before penalty starts accruing.</p>
            </div>
            <div className="md:col-span-2 space-y-2">
              <Input
                type="number"
                step="1"
                min="0"
                value={settings.default_grace_period_days}
                onChange={(e) => setSettings({ ...settings, default_grace_period_days: parseInt(e.target.value || "0", 10) })}
                className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-[200px]"
              />
              <p className="text-xs text-zinc-500">
                <span className="text-zinc-400">3-7 days</span> is typical. <span className="text-zinc-400">0</span> = penalty starts the day after due date.{" "}
                Note: this is separate from <span className="font-mono text-zinc-400">payment_terms_days</span> (=5d), which is the time between sending the invoice and the due date itself.
              </p>
            </div>
          </div>

          <div className="border-t border-zinc-800" />

          {/* Base */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div className="md:col-span-1">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Wallet className="h-3.5 w-3.5 text-emerald-400" /> Base
              </Label>
              <p className="text-xs text-zinc-500 mt-1">What the penalty % is applied to.</p>
            </div>
            <div className="md:col-span-2 space-y-2">
              {BASE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    settings.default_base === opt.value
                      ? "border-orange-500/40 bg-orange-500/5"
                      : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="default_base"
                    value={opt.value}
                    checked={settings.default_base === opt.value}
                    onChange={() => setSettings({ ...settings, default_base: opt.value })}
                    className="mt-1 accent-orange-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-zinc-50">{opt.label}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800" />

          {/* Note */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div className="md:col-span-1">
              <Label className="text-zinc-300">Manager note</Label>
              <p className="text-xs text-zinc-500 mt-1">Optional context shown next to overdue invoices in the cashflow page.</p>
            </div>
            <div className="md:col-span-2">
              <Textarea
                rows={3}
                value={settings.note}
                onChange={(e) => setSettings({ ...settings, note: e.target.value })}
                placeholder="e.g. Default reflects clauses in Smartorange template contract v3, July 2025."
                className="bg-zinc-950 border-zinc-800 text-zinc-50"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Worked example */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
            <Flame className="h-4 w-4 text-amber-500" /> Worked example
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-zinc-300">
            Invoice for <span className="font-mono text-zinc-50">${exampleAmount.toLocaleString()}</span>,{" "}
            <span className="font-mono text-zinc-50">{exampleDaysLate}</span> days past due.
          </p>
          <p className="text-sm text-zinc-300">
            Grace = <span className="font-mono text-zinc-50">{settings.default_grace_period_days}d</span> →{" "}
            charged for <span className="font-mono text-zinc-50">{daysCharged}</span> days at{" "}
            <span className="font-mono text-zinc-50">{settings.default_rate_per_day}%</span> per day on the {settings.default_base === "invoice_amount" ? "invoice amount" : "contract total"}.
          </p>
          <p className="text-base text-zinc-50">
            Penalty ={" "}
            <Badge variant="outline" className="font-mono text-base bg-red-500/10 text-red-300 border-red-500/40 px-2 py-0.5">
              ${examplePenalty.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </Badge>
          </p>
          {!settings.enabled && (
            <p className="text-xs text-amber-400 mt-2">
              Penalty calculation is currently <span className="font-semibold">off</span>. Turn it on at the top to apply this on overdue invoices.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Override hint */}
      <Card className="bg-zinc-900/30 border-zinc-800/60">
        <CardContent className="p-4 text-xs text-zinc-500">
          <span className="text-zinc-400 font-semibold">Override per project:</span> open any sales project and set{" "}
          <span className="font-mono text-zinc-400">penalty_rate_per_day</span> /{" "}
          <span className="font-mono text-zinc-400">grace_period_days</span> directly on the project. Project values win over portal defaults; the base ({settings.default_base === "invoice_amount" ? "invoice" : "contract"}) is portal-wide.
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentPenaltySettingsPage;
